/**
 * Config schema used to add a redirect (server-side in server.js and client-side in App.js) and a link in HomePage.js
 * @typedef {Object} RedirectConfig
 * @property {string} from - The original path (excluding /campaign), used in snowApp.get and zion-router’s <Route path={from} />
 * @property {string} to - The absolute or relative path (relative to familysearch.org, *not* to /campaign) to redirect to
 * @property {boolean} [preservePath] - When true, trailing path segments and query params are preserved and appended to `to`
 */

/**
 * Add new redirects to this array
 * @type {Array.<RedirectConfig>}
 */
const redirects = [
  // Expired RootsTech campaigns
  { from: '/share', to: '/connect/famous' },
  { from: '/rootstechregistration', to: '/rootstech/registration' },
  { from: '/page/easyreg*', to: '/rootstech/registration/online/easy-registration' },
  // Old admin tool paths
  { from: '/tsa', to: '/campaign/admin/tsa' },
  { from: '/tools/notifications/image', to: '/campaign/admin/notifications/image' },
  // Old campaign paths with lingering links from the Church or other websites
  { from: '/mobile/tree', to: '/mobile-apps' },
  { from: '/patrons', to: '/help/helper' },
  // Vanity redirects
  { from: '/blackhistorymonth', to: '/blog/black-history-month' },
  { from: '/pioneer', to: '/campaign/pioneers' },
  { from: '/mypioneers', to: '/campaign/pioneers' },
  { from: '/contributor', to: '/campaign/contributors' },
  { from: '/photocollages', to: '/campaign/photocollage' },
  { from: '/lookalikes', to: '/campaign/lookalike' },
  { from: '/mymatches', to: '/campaign/mymatch' },
  { from: '/freefamilytrees', to: '/tree-designs/global' },
  { from: '/freefamilytree', to: '/tree-designs/global' },
  { from: '/keepsakes', to: '/tree-designs/global' },
  { from: '/mymemories', to: '/campaign/newmemories' },
  { from: '/eternalfamily', to: '/campaign/family-ordinance-opportunities' },
  { from: '/fandancy', to: '/campaign/family-ordinance-opportunities' },
  { from: '/rolluptest', to: '/campaign/family-ordinance-opportunities' },
  { from: '/temple-ordinances', to: '/campaign/family-ordinance-opportunities' },
  { from: '/temple-ord-ready', to: '/campaign/temple-opportunity' },
  { from: '/family-ordinance-opportunities/remaining', to: '/campaign/remaining-ordinances' },
  // Redirects to trigger the native mobile app deep link, so patrons don’t have to sign in again
  { from: '/famous', to: '/connect/famous', preservePath: true },
]

const getLangPrefix = (req) => {
  const lang = req.header('x-orig-lang')
  return lang ? `/${lang}` : ''
}

/**
 * Builds the redirect URL for a given redirect config entry and incoming request.
 * @param {RedirectConfig} redirect
 * @param {Object} req - incoming Express/Snow request object
 * @returns {string} the path to redirect to
 */
const buildRedirectUrl = (redirect, req) => {
  const { to, preservePath } = redirect
  const langPrefix = getLangPrefix(req)
  if (preservePath) {
    const trailingPath = req.params[0] ? `/${req.params[0]}` : ''
    const queryString = req.originalUrl.includes('?') ? req.originalUrl.slice(req.originalUrl.indexOf('?')) : ''
    return `${langPrefix}${to}${trailingPath}${queryString}`
  }
  return `${langPrefix}${to}`
}

// Using module.exports for compatibility with the node server.js file
module.exports = { redirects, buildRedirectUrl, getLangPrefix }
